"use client";
import { AnimatedStats } from "./AnimatedStats";
import { Button } from "../Button";
import Link from "next/link";
import Image from "next/image";
import { useEffect, useRef } from "react";
import { gsap } from "@/lib/gsap";

interface WhyChooseUsSectionProps {
  data: {
    title?: string;
    testimonial?: {
      quote?: string;
      author?: string;
      title?: string;
    };
    stats?: Array<{
      id: number;
      label: string;
      value: number | null;
      suffix: string | null;
      description: string | null;
      animation: {
        start: number;
        end: number;
      };
    }>;
    image?: {
      src: string;
      alt: string;
    };
    button?: {
      text: string;
      href: string;
    };
  };
}

export default function WhyChooseUsSection({ data }: WhyChooseUsSectionProps) {
  const { title, testimonial, stats, image, button } = data;

  const headerRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (!headerRef.current) return;

    gsap.set(headerRef.current, {
      opacity: 0,
      y: 30,
      filter: "blur(8px)",
    });

    const ctx = gsap.context(() => {
      gsap.to(headerRef.current, {
        opacity: 1,
        y: 0,
        filter: "blur(0px)",
        duration: 0.8,
        ease: "power2.out",
        scrollTrigger: {
          trigger: headerRef.current,
          start: "top 100%",
          end: "top 40%",
          toggleActions: "play none none reverse",
        },
      });
    }, headerRef);

    return () => ctx.revert();
  }, []);

  const blockquoteRef = useRef<HTMLQuoteElement>(null);

  useEffect(() => {
    const element = blockquoteRef.current;
    if (!element) return;

    gsap.set(element, {
      opacity: 0,
      x: -30,
    });

    gsap.to(element, {
      opacity: 1,
      x: 0,
      duration: 0.8,
      ease: "power2.out",
      scrollTrigger: {
        trigger: element,
        start: "top 100%",
        end: "top 40%",
        toggleActions: "play none none reverse",
      },
      id: "blockquote-animation",
    });
  }, []);

  const statRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = statRef.current;
    if (!element) return;

    gsap.set(element, {
      opacity: 0,
      y: 20,
    });

    gsap.to(element, {
      opacity: 1,
      y: 0,
      duration: 0.6,
      ease: "power2.out",
      scrollTrigger: {
        trigger: element,
        start: "top 100%",
        end: "top 40%",
        toggleActions: "play none none none",
      },
    });
  }, []);

  const buttonRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    const element = buttonRef.current;
    if (!element) return;

    gsap.set(element, {
      opacity: 0,
      y: 20,
    });

    gsap.to(element, {
      opacity: 1,
      y: 0,
      duration: 0.6,
      ease: "power2.out",
      scrollTrigger: {
        trigger: element,
        start: "top 100%",
        toggleActions: "play none none reverse",
      },
    });
  }, []);

  const thumbnailRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const element = thumbnailRef.current;
    if (!element) return;

    gsap.set(element, {
      opacity: 0,
      y: 40,
    });

    gsap.to(element, {
      opacity: 1,
      y: 0,
      duration: 0.8,
      ease: "power2.out",
      scrollTrigger: {
        trigger: element,
        start: "top 100%",
        end: "top 40%",
        toggleActions: "play none none reverse",
      },
    });
  }, []);

  return (
    <section className="bg-[#F5F5F5] py-16 md:py-24">
      <div className="container">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div
            ref={thumbnailRef}
            className="relative group rounded-lg w-full max-w-[600px] mx-auto"
          >
            <div className="absolute inset-0 bg-[#00000066] rounded-lg"></div>
            {image?.src && (
              <Image
                src={image.src}
                alt={image.alt}
                width={600}
                height={400}
                quality={85}
                sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 600px"
                className="rounded-lg shadow-2xl w-full h-auto object-cover"
              />
            )}
          </div>

          {}

          <div ref={headerRef}>
            {title && (
              <h2 className="  text-4xl md:text-5xl font-semibold mb-6 text-theme-secondary">
                {title}
              </h2>
            )}

            {testimonial && (
              <blockquote
                ref={blockquoteRef}
                className="  border-l-4 border-orange-500 pl-6 mb-8"
              >
                {testimonial.quote && (
                  <p className="text-[18px] md:text-[20px] leading-[28px] font-medium text-[#4F5051]">
                    &quot;{testimonial.quote}&quot;
                  </p>
                )}
                {(testimonial.author || testimonial.title) && (
                  <cite className="block mt-4 text-[#4F5051] text-[14px] md:text-[15.125px] font-medium leading-[24px] not-italic">
                    - {testimonial.author}
                    {testimonial.title && `, ${testimonial.title}`}
                  </cite>
                )}
              </blockquote>
            )}

            {stats && stats.length > 0 && (
              <div ref={statRef}>
                <AnimatedStats stats={stats} />
              </div>
            )}

            {button && (
              <Link
                ref={buttonRef}
                href={button.href}
                className="inline-block mt-6 md:mt-8"
              >
                <Button className="cursor-pointer transform hover:scale-105 hover:bg-orange-400 transition-all duration-300 relative overflow-hidden group before:content-[''] before:absolute before:top-0 before:left-[-100%] before:w-full before:h-full before:bg-gradient-to-r before:from-transparent before:via-white/20 before:to-transparent before:transition-[left] before:duration-500 hover:before:left-[100%]">
                  <span className="transition-all duration-300 text-white group-hover:text-white relative z-10">
                    {button.text}
                  </span>
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
